/**
 * Lexer for the proof. annotation grammar.
 *
 * Converts raw source text into a sequence of classified tokens.
 * Used by both the parser (to build the AST) and the syntax
 * highlighter (to colour the editor).
 *
 * Design:
 * - Line-by-line scan with a simple state machine.
 * - A @ at start-of-line introduces a section, directive, or paren-directive.
 * - # at start-of-line is a markdown heading (scope).
 * - //, block-comment, and HTML comment are comment forms.
 * - label: value anywhere on a line is a labelled-line candidate.
 * - Numbers + common units are auto-detected in prose.
 *
 * Pure TS -- no DOM, no Node built-ins. See architecture.md section 5.1.
 */

import type { Token, TokenClass, Span, Position } from "../ast/types.js";

// ---------------------------------------------------------------------------
// Exported public types
// ---------------------------------------------------------------------------

export type { Token, TokenClass };

// ---------------------------------------------------------------------------
// Reserved directives (imperative, single-line -- never a section block)
// ---------------------------------------------------------------------------

/**
 * These keywords after @ are directives, not section headers.
 * They take optional trailing args but never open a child block.
 */
export const RESERVED_DIRECTIVES = new Set(["end", "scale"]);

/**
 * Track 2.A grammar-construct directives: `@bind`, `@define`, `@define-section`.
 *
 * These are single-line directive forms — they declare a virtual variable, a
 * named formula, or a section type — and, crucially, they must NEVER open a
 * section block. Before this set existed, `@bind` fell through to the generic
 * `section-keyword` branch below, which made the editor treat it as a phantom
 * section header (swallowing every following labelled line's evaluation) and
 * mis-highlighted the line in Ember accent as a section keyword.
 *
 * They are lexed to the dedicated `bind-directive` / `define-directive` token
 * classes so the syntax highlighter (SyntaxDecorations) styles them via
 * `.tok-bind-directive` / `.tok-define-directive` and never as a section header.
 * The parser (parser/index.ts) attaches their nodes to the enclosing section's
 * children WITHOUT closing the section.
 *
 * Spec: design.md §7.7, extensibility-design.md §3 / §5.3.
 */
export const GRAMMAR_CONSTRUCT_DIRECTIVES: ReadonlyMap<
  string,
  "bind-directive" | "define-directive"
> = new Map([
  ["bind", "bind-directive"],
  ["define", "define-directive"],
  ["define-section", "define-directive"],
]);

// ---------------------------------------------------------------------------
// Common units that trigger auto-detection in prose
// ---------------------------------------------------------------------------

// Unit tokens. Uses unicode escapes to avoid non-ASCII in the source file.
// degC = °C, degF = °F, deg = °
const DEG = "°";
const UNIT_ALTS = [
  `${DEG}C`,
  `${DEG}F`,
  "ml",
  "kg",
  "cl",
  "dl",
  "tbsp",
  "tsp",
  "min",
  "sec",
  DEG,
  "%",
  "g",
  "l",
  "h",
  "s",
  "m",
].join("|");

const UNIT_PATTERN_SOURCE = `(?:${UNIT_ALTS})`;

/**
 * Full pattern for an auto-detected prose value span: number + optional unit.
 * E.g. "400g", "65%", "24h", "475degC", "90s", "10 min".
 */
export const PROSE_VALUE_RE = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*(${UNIT_PATTERN_SOURCE})?`, "gu");

// ---------------------------------------------------------------------------
// Inline math pattern: <number><unit>/<number>=<number><unit>
// ---------------------------------------------------------------------------

// Build inline math pattern from ASCII-safe parts
const NUM = "\\d+(?:\\.\\d+)?";
const OPT_UNIT = `(?:${UNIT_ALTS})?`;

export const INLINE_MATH_RE = new RegExp(
  `${NUM}${OPT_UNIT}\\s*[/*]\\s*${NUM}\\s*=\\s*${NUM}${OPT_UNIT}`,
  "u",
);

// ---------------------------------------------------------------------------
// Block UUID pattern: #b-<uuid>
// ---------------------------------------------------------------------------

const BLOCK_UUID_RE =
  /#b-([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/u;

// ---------------------------------------------------------------------------
// Lex result
// ---------------------------------------------------------------------------

export interface LexResult {
  tokens: Token[];
  /** Source lines (split on newline), for parser use. */
  lines: string[];
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

/**
 * Lex source into a flat token sequence.
 *
 * The lexer is intentionally simple (line-by-line + regex); the parser
 * is responsible for building tree structure from the token stream.
 */
export function lex(source: string): LexResult {
  const lines = source.split("\n");
  const tokens: Token[] = [];
  let lineIdx = 0;

  while (lineIdx < lines.length) {
    const line = lines[lineIdx] ?? "";
    const lineNo = lineIdx + 1; // 1-based

    // Block comment spanning multiple lines: detect opening /*
    if (/^\s*\/\*/u.test(line)) {
      const startCol = line.indexOf("/*");
      const start: Position = { line: lineNo, column: startCol };

      let combined = line;
      let endLineIdx = lineIdx;
      while (endLineIdx < lines.length && !combined.includes("*/", combined.indexOf("/*") + 2)) {
        endLineIdx++;
        combined += "\n" + (lines[endLineIdx] ?? "");
      }
      const endLine = endLineIdx + 1;
      const rawComment = combined.slice(combined.indexOf("/*"));
      const closeIdx = rawComment.indexOf("*/");
      const rawTrimmed = rawComment.slice(0, closeIdx + 2);
      const end: Position = { line: endLine, column: rawTrimmed.length };
      pushToken(tokens, "comment", rawTrimmed, { start, end });
      lineIdx = endLineIdx + 1;
      continue;
    }

    // HTML comment: <!-- ... -->
    if (/^\s*<!--/u.test(line)) {
      const startCol = line.indexOf("<!--");
      const start: Position = { line: lineNo, column: startCol };

      let combined = line;
      let endLineIdx = lineIdx;
      while (endLineIdx < lines.length && !combined.includes("-->", combined.indexOf("<!--") + 4)) {
        endLineIdx++;
        combined += "\n" + (lines[endLineIdx] ?? "");
      }
      const endLine = endLineIdx + 1;
      const raw = combined.slice(combined.indexOf("<!--"));
      const closeIdx = raw.indexOf("-->");
      const rawTrimmed = raw.slice(0, closeIdx + 3);
      const end: Position = { line: endLine, column: rawTrimmed.length };
      pushToken(tokens, "comment", rawTrimmed, { start, end });
      lineIdx = endLineIdx + 1;
      continue;
    }

    // Line comment: // (only when at start of line or after whitespace)
    if (/^(\s*\/\/)/u.test(line)) {
      const startCol = line.indexOf("//");
      const start: Position = { line: lineNo, column: startCol };
      const end: Position = { line: lineNo, column: line.length };
      pushToken(tokens, "comment", line.slice(startCol), { start, end });
      lineIdx++;
      continue;
    }

    // Markdown heading -> scope
    const headingMatch = /^(#{1,6})\s+(.+)$/u.exec(line);
    if (headingMatch) {
      const hashes = headingMatch[1] ?? "";
      const text = headingMatch[2] ?? "";
      const start: Position = { line: lineNo, column: 0 };
      const end: Position = { line: lineNo, column: line.length };
      pushToken(tokens, "scope-heading", `${hashes} ${text}`, { start, end });
      lineIdx++;
      continue;
    }

    // @ at start-of-line (after optional whitespace)
    const atMatch = /^(\s*)@([a-zA-Z][a-zA-Z0-9_-]*)(.*)$/u.exec(line);
    if (atMatch) {
      const indent = atMatch[1] ?? "";
      const keyword = atMatch[2] ?? "";
      const rest = atMatch[3] ?? "";
      const col = indent.length; // position of @

      const constructClass = GRAMMAR_CONSTRUCT_DIRECTIVES.get(keyword);
      if (RESERVED_DIRECTIVES.has(keyword)) {
        // Directive
        const start: Position = { line: lineNo, column: col };
        const end: Position = { line: lineNo, column: line.length };
        pushToken(tokens, "directive", `@${keyword}${rest}`, { start, end });
      } else if (constructClass !== undefined) {
        // Track 2.A grammar-construct directive (@bind / @define / @define-section).
        // Emitted as a single directive-class token so the highlighter never
        // treats it as a section header and the parser never opens a block.
        const start: Position = { line: lineNo, column: col };
        const end: Position = { line: lineNo, column: line.length };
        pushToken(tokens, constructClass, `@${keyword}${rest}`, { start, end });
      } else if (rest.trimStart().startsWith("(")) {
        // Paren-form directive / paren-form section header
        const start: Position = { line: lineNo, column: col };
        const end: Position = { line: lineNo, column: line.length };
        // Emit the name
        pushToken(tokens, "paren-directive-name", `@${keyword}`, {
          start,
          end: { line: lineNo, column: col + keyword.length + 1 },
        });
        // Emit the paren content
        const parenStart = rest.indexOf("(");
        pushToken(tokens, "paren-directive-content", rest.slice(parenStart), {
          start: { line: lineNo, column: col + keyword.length + 1 + parenStart },
          end,
        });
      } else {
        // Section keyword (possible inline label, block UUID, or paren args on the right)
        const start: Position = { line: lineNo, column: col };
        const end: Position = { line: lineNo, column: line.length };
        pushToken(tokens, "section-keyword", `@${keyword}${rest}`, { start, end });

        // Extract block UUID if present
        const uuidMatch = BLOCK_UUID_RE.exec(rest);
        if (uuidMatch) {
          const uuidStart = line.indexOf("#b-");
          pushToken(tokens, "block-uuid", uuidMatch[0], {
            start: { line: lineNo, column: uuidStart },
            end: { line: lineNo, column: uuidStart + uuidMatch[0].length },
          });
        }
      }
      lineIdx++;
      continue;
    }

    // Blank line
    if (/^\s*$/u.test(line)) {
      const start: Position = { line: lineNo, column: 0 };
      const end: Position = { line: lineNo, column: 0 };
      pushToken(tokens, "prose", "", { start, end });
      lineIdx++;
      continue;
    }

    // Labelled line: `label: value`
    const colonIdx = findLabelColon(line);
    if (colonIdx > 0) {
      const label = line.slice(0, colonIdx).trimEnd();
      const valueRaw = line.slice(colonIdx + 1).trimStart();
      const start: Position = { line: lineNo, column: 0 };
      const end: Position = { line: lineNo, column: line.length };

      // Label token
      pushToken(tokens, "label", label, {
        start,
        end: { line: lineNo, column: colonIdx },
      });

      // Value tokens
      lexValue(tokens, valueRaw, lineNo, colonIdx + 1);

      void end; // end is computed above for potential future use
    } else {
      // Plain prose line
      const start: Position = { line: lineNo, column: 0 };
      const end: Position = { line: lineNo, column: line.length };
      pushToken(tokens, "prose", line, { start, end });
    }

    lineIdx++;
  }

  return { tokens, lines };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pushToken(tokens: Token[], cls: TokenClass, text: string, span: Span): void {
  tokens.push({ class: cls, text, span });
}

/**
 * Find the index of the first : on a line that looks like a label separator.
 *
 * Rules:
 * - Must have at least one character before it (the label).
 * - Must not be inside :// (URLs like https://...).
 * - Must not start the line.
 */
function findLabelColon(line: string): number {
  for (let i = 1; i < line.length; i++) {
    if (line[i] === ":") {
      // Reject ://
      if (line[i + 1] === "/" && line[i + 2] === "/") continue;
      return i;
    }
  }
  return -1;
}

/**
 * Lex a value string (right-hand side of a labelled line) into tokens.
 * Emits: inline-math, or prose.
 */
function lexValue(tokens: Token[], value: string, lineNo: number, colOffset: number): void {
  // Check for inline math first
  if (INLINE_MATH_RE.test(value)) {
    const start: Position = { line: lineNo, column: colOffset };
    const end: Position = { line: lineNo, column: colOffset + value.length };
    pushToken(tokens, "inline-math", value, { start, end });
    return;
  }

  const start: Position = { line: lineNo, column: colOffset };
  const end: Position = { line: lineNo, column: colOffset + value.length };
  pushToken(tokens, "prose", value, { start, end });
}
